import { Menu, Select } from 'antd'
import { useSelector } from 'react-redux'
import { faGlobeEurope, faIdBadge, faSchool, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'

import StudentStatusSelector from '../../../Components/shared/Selectors/StudentStatusSelector'
import { isNonEmptyArray } from '../../../utils'
import IconLabel, { FontSizes } from '../../../Components/icons/IconLabel'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { BadgeStateLabels, BadgeStates, canSeeBadges } from '../../../utils/entities/badge'
import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import NationalitiesSelector from '../../../Components/shared/Selectors/NationalitiesSelector'

const SELECTOR_STYLE = { width: '210px' }
/**
 * A vertical menu clamps its items to 40px and clips what overflows. The selectors are in multiple
 * mode and grow as their tags wrap - the schools one from its first tag, its rendering being on
 * several lines - so the items have to follow instead of truncating them.
 */
const MENU_ITEM_STYLE = { cursor: 'default', height: 'auto', lineHeight: 1.5, overflow: 'visible', padding: '4px 16px' }

/**
 * Only the filters owned by this menu: `columns` and `search` also live in `parameters.filters` and
 * must survive a reset, so they are merged back by the views instead of being listed here.
 */
export const DEFAULT_STUDENT_FILTERS = Object.freeze({
  badge: null,
  nationalities: [],
  schools: [],
  status: []
})

export const getActiveFiltersCount = filters => {
  const filtersState = filters ?? {}
  let count = 0

  if (isNonEmptyArray(filtersState.status)) {
    count += 1
  }

  if (isNonEmptyArray(filtersState.schools)) {
    count += 1
  }

  if (isNonEmptyArray(filtersState.nationalities)) {
    count += 1
  }

  if (filtersState.badge) {
    count += 1
  }

  return count
}

const StudentFilters = ({ filters, onFiltersChange }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const displayBadgeFilter = canSeeBadges(user)

  const handleFiltersChange = (key, value) => onFiltersChange({ ...filters, [key]: value })

  // No horizontal padding on the wrapper: the menu items and the group titles already carry their own 16px.
  return (
    <div style={{ padding: '8px 0' }}>
      {/* Nested in the dropdown's own menu: its vertical mode border would draw a stray line on the right. */}
      <Menu inlineIndent={0} style={{ border: 'none' }}>
        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faUserGraduate} text={t('common.status')} />}>
          <Menu.Item key='status' disabled style={MENU_ITEM_STYLE}>
            <StudentStatusSelector
              multiple='multiple'
              placeholder={t('common.status')}
              style={SELECTOR_STYLE}
              tagPlaceholder={false}
              values={filters?.status}
              onChange={value => handleFiltersChange('status', value)}
            />
          </Menu.Item>
        </Menu.ItemGroup>

        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faSchool} text={t('common.school')} />}>
          <Menu.Item key='schools' disabled style={MENU_ITEM_STYLE}>
            <SchoolsSelector
              multiple='multiple'
              placeholder={t('common.schools')}
              style={SELECTOR_STYLE}
              tagPlaceholder={false}
              values={filters?.schools}
              onChange={value => handleFiltersChange('schools', value)}
            />
          </Menu.Item>
        </Menu.ItemGroup>

        <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faGlobeEurope} text={t('common.nationality')} />}>
          <Menu.Item key='nationalities' disabled style={MENU_ITEM_STYLE}>
            <NationalitiesSelector
              multiple='multiple'
              placeholder={t('common.nationality')}
              style={SELECTOR_STYLE}
              tagPlaceholder={false}
              values={filters?.nationalities}
              onChange={value => handleFiltersChange('nationalities', value)}
            />
          </Menu.Item>
        </Menu.ItemGroup>

        {displayBadgeFilter && (
          <Menu.ItemGroup title={<IconLabel fontSize={FontSizes.SMALL} padding={0} icon={faIdBadge} text={t('common.badge')} />}>
            <Menu.Item key='badge' disabled style={MENU_ITEM_STYLE}>
              <Select
                allowClear
                style={SELECTOR_STYLE}
                value={filters?.badge ?? undefined}
                placeholder={t('common.badge')}
                onChange={value => handleFiltersChange('badge', value ?? null)}
              >
                {Object.values(BadgeStates).map(state => (
                  <Select.Option key={state} value={state}>
                    {t(BadgeStateLabels[state])}
                  </Select.Option>
                ))}
              </Select>
            </Menu.Item>
          </Menu.ItemGroup>
        )}
      </Menu>
    </div>
  )
}

export default StudentFilters
